import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Upload, X, User } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import { useChatwootToken } from "@/hooks/use-chatwoot-token";
import { logCrud } from "@/services/activities";

const userSchema = z.object({
  nome_completo: z.string().min(1, "Nome completo é obrigatório"),
  email: z.string().email("Email inválido"),
  telefone: z.string().optional(),
  cargo: z.string().optional(),
  status: z.string().default("ativo"),
  data_nascimento: z.string().optional(),
  password: z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    z.string().min(6, "Senha deve ter no mínimo 6 caracteres").optional(),
  ),
  role_id: z.string().optional(),
  tenant_id: z.string().optional(),
});

type UserFormData = z.infer<typeof userSchema>;

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    nome_completo: string;
    email: string;
    telefone: string | null;
    cargo: string | null;
    status: string | null;
    data_nascimento: string | null;
    role_id: string | null;
    tenant_id: string | null;
    foto_url?: string | null;
    chatwoot_avatar_url?: string | null;
    roles?: { name: string; slug: string };
    tenants?: { name: string; slug: string };
  } | null;
  onCreated?: (user: { id: string; nome_completo: string }) => void;
}

export function UserModal({ isOpen, onClose, user, onCreated }: UserModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!user;
  const { hasToken } = useChatwootToken();
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(null);
  const [shouldRemovePhoto, setShouldRemovePhoto] = useState(false);

  // Fetch available roles from roles table
  const { data: availableRoles } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles" as any)
        .select("id, name, slug")
        .order("name", { ascending: true }) as any;
      
      if (error) throw error;
      return data as { id: string; name: string; slug: string }[];
    },
  });

  const { tenantId } = useTenant();

  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      nome_completo: "",
      email: "",
      telefone: "",
      cargo: "",
      status: "ativo",
      data_nascimento: "",
      password: "",
      role_id: "",
      tenant_id: "",
    },
  });

  // Máscara para telefone brasileiro
  const formatPhoneBR = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 10) {
      // (##) ####-####
      return digits
        .replace(/^(\d{0,2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2")
        .slice(0, 14);
    }
    // (##) #####-#### para 11 dígitos
    return digits
      .replace(/^(\d{0,2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .slice(0, 15);
  };

  // Função para upload de foto
  const uploadPhoto = async (file: File, userId: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('user-photos')
      .upload(filePath, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('user-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  // Função para remover foto antiga
  const removeOldPhoto = async (url: string | null) => {
    if (!url || !url.includes('user-photos')) return;
    
    const path = url.split('user-photos/')[1]?.split('?')[0];
    if (!path) return;

    try {
      await supabase.storage
        .from('user-photos')
        .remove([path]);
    } catch (error) {
      console.error("Erro ao remover foto antiga:", error);
    }
  };

  useEffect(() => {
    // Resetar estados quando o usuário muda (quando o modal é aberto)
    if (user) {
      // Mostrar preview apenas se houver foto real (upload ou Chatwoot), não Gravatar
      const realPhoto = user.foto_url || user.chatwoot_avatar_url || null;
      setCurrentPhotoUrl(user.foto_url || null);
      setPhotoPreview(realPhoto);
      setShouldRemovePhoto(false);
      setSelectedPhoto(null);
      
      form.reset({
        nome_completo: user.nome_completo,
        email: user.email,
        telefone: user.telefone ? formatPhoneBR(user.telefone) : "",
        cargo: user.cargo || "",
        status: user.status || "ativo",
        data_nascimento: user.data_nascimento || "",
        password: "",
        role_id: user.role_id || "",
        tenant_id: user.tenant_id || "",
      });
    } else {
      setCurrentPhotoUrl(null);
      setPhotoPreview(null);
      setSelectedPhoto(null);
      setShouldRemovePhoto(false);
      form.reset({
        nome_completo: "",
        email: "",
        telefone: "",
        cargo: "",
        status: "ativo",
        data_nascimento: "",
        password: "",
        role_id: "",
        tenant_id: "",
      });
    }
  }, [user]);

  // Handler para seleção de foto
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Por favor, selecione uma imagem válida");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5MB");
      return;
    }

    setSelectedPhoto(file);
    setShouldRemovePhoto(false); // Se está adicionando nova foto, não remover
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handler para remover foto
  const handleRemovePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setShouldRemovePhoto(true); // Marcar que a foto deve ser removida
  };

  const mutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      // Bloquear explicitamente o envio com perfil 'Admin Saas'
      const selectedRole = availableRoles?.find(r => r.id === data.role_id);
      if (selectedRole && selectedRole.slug === "admin_saas") {
        throw new Error("Perfil 'Admin Saas' não pode ser atribuído a usuários por esta interface.");
      }

      let photoUrl: string | null | undefined = user?.foto_url || null;

      if (isEditing && user) {
        // Se o usuário clicou para remover a foto
        if (shouldRemovePhoto) {
          if (user.foto_url && user.foto_url.includes('user-photos')) {
            await removeOldPhoto(user.foto_url);
          }
          photoUrl = null;
        } else if (selectedPhoto) {
          // Se está adicionando uma nova foto
          setIsUploadingPhoto(true);
          try {
            // Remover foto antiga se houver
            if (user.foto_url && user.foto_url.includes('user-photos')) {
              await removeOldPhoto(user.foto_url);
            }
            photoUrl = await uploadPhoto(selectedPhoto, user.id);
          } catch (error: any) {
            setIsUploadingPhoto(false);
            throw new Error(`Erro ao fazer upload da foto: ${error.message}`);
          } finally {
            setIsUploadingPhoto(false);
          }
        }

        // Atualizar via Edge Function (usa service role)
        const { data: fnData, error: fnError } = await supabase.functions.invoke("admin-update-user", {
          body: {
            id: user.id,
            nome_completo: data.nome_completo,
            telefone: data.telefone || null,
            cargo: data.cargo || null,
            status: data.status,
            data_nascimento: data.data_nascimento || null,
            role_id: data.role_id || null,
            tenant_id: tenantId || null,
            foto_url: photoUrl,
          },
        });
        if (fnError) throw fnError;
        if (!fnData?.success) throw new Error("Falha ao atualizar usuário (função)");

        if (hasToken) {
          try {
            const sdk = getChatwootSDK();
            await sdk.syncContact("profile", user.id);
          } catch (syncError: any) {
            console.error("Erro ao sincronizar usuário com Chatwoot", syncError);
            toast.warning("Usuário atualizado, mas não foi possível sincronizar com o Chatwoot.");
          }
        }
        return { user_id: user.id };
      } else {
        // Create new user via Edge Function (usa service role no backend)
        if (!data.password) {
          throw new Error("Senha é obrigatória para novos usuários");
        }

        const { data: fnData, error: fnError } = await supabase.functions.invoke("admin-create-user", {
          body: {
            email: data.email,
            password: data.password,
            nome_completo: data.nome_completo,
            telefone: data.telefone || null,
            cargo: data.cargo || null,
            status: data.status,
            data_nascimento: data.data_nascimento || null,
            role_id: data.role_id || null,
            tenant_id: tenantId || null,
          },
        });

        if (fnError) throw fnError;
        if (!fnData?.success) throw new Error("Falha ao criar usuário (função)");

        const createdUserId = fnData.user_id as string | undefined;
        
        // Se temos uma foto selecionada, fazer upload agora que temos o ID do usuário
        if (createdUserId && selectedPhoto) {
          setIsUploadingPhoto(true);
          try {
            const uploadedUrl = await uploadPhoto(selectedPhoto, createdUserId);
            // Atualizar foto_url via Edge Function
            const { error: updatePhotoError } = await supabase.functions.invoke("admin-update-user", {
              body: {
                id: createdUserId,
                foto_url: uploadedUrl,
              },
            });
            
            if (updatePhotoError) {
              console.error("Erro ao atualizar foto_url:", updatePhotoError);
              toast.warning("Usuário criado, mas não foi possível salvar a foto.");
            } else {
              photoUrl = uploadedUrl;
            }
          } catch (photoError: any) {
            console.error("Erro ao fazer upload da foto após criação:", photoError);
            toast.warning("Usuário criado, mas não foi possível fazer upload da foto.");
          } finally {
            setIsUploadingPhoto(false);
          }
        }

        if (createdUserId && hasToken) {
          try {
            const sdk = getChatwootSDK();
            await sdk.syncContact("profile", createdUserId);
          } catch (syncError: any) {
            console.error("Erro ao sincronizar usuário com Chatwoot", syncError);
            toast.warning("Usuário criado, mas não foi possível sincronizar com o Chatwoot.");
          }
        }
        return fnData;
      }
    },
    onSuccess: (res) => {
      // Recarregar listagem de usuários imediatamente (todas as variações do key "users")
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      toast.success(isEditing ? "Usuário atualizado com sucesso!" : "Usuário criado com sucesso!");
      if (isEditing && user?.id) {
        void logCrud({ entitySlug: "entity:profiles", operationSlug: "update", entityId: user.id, entityName: form.getValues("nome_completo"), screenSlug: "usuarios" });
      } else if (!isEditing && res && (res as any).user_id) {
        void logCrud({ entitySlug: "entity:profiles", operationSlug: "create", entityId: (res as any).user_id, entityName: form.getValues("nome_completo"), screenSlug: "usuarios" });
      }
      if (!isEditing && res && (res as any).user_id) {
        onCreated?.({ id: (res as any).user_id, nome_completo: form.getValues("nome_completo") });
      }
      // Limpar estados
      setSelectedPhoto(null);
      setPhotoPreview(null);
      setIsUploadingPhoto(false);
      setShouldRemovePhoto(false);
      form.reset();
      onClose();
    },
    onError: (error) => {
      toast.error("Erro ao salvar usuário: " + error.message);
    },
  });

  const onSubmit = (data: UserFormData) => {
    mutation.mutate(data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <div className="flex max-h[90vh] md:max-h-[90vh] flex-col">
          <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
            <DialogTitle>{isEditing ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onClose()}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <Form {...form}>
              <form id="user-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Foto do Usuário e Campos Principais */}
            <div className="flex gap-6 items-start">
              {/* Foto do Usuário */}
              <div className="flex-shrink-0">
                <Label className="mb-2 block">Foto do Usuário</Label>
                <Input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  disabled={mutation.isPending || isUploadingPhoto}
                />
                {photoPreview ? (
                  <div className="relative">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-32 h-32 rounded-full object-cover border-2 border-border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => document.getElementById('photo-upload')?.click()}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePhoto();
                      }}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90 z-10"
                      disabled={mutation.isPending || isUploadingPhoto}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div 
                    className="w-32 h-32 rounded-full bg-muted border-2 border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
                    onClick={() => document.getElementById('photo-upload')?.click()}
                  >
                    <User className="h-16 w-16 text-muted-foreground" />
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Clique para adicionar
                </p>
                {isUploadingPhoto && (
                  <p className="text-xs text-muted-foreground mt-1 text-center">Fazendo upload...</p>
                )}
              </div>

              {/* Campos ao lado da foto */}
              <div className="flex-1 space-y-4">
                {/* Primeira linha: Nome Completo e Email */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="nome_completo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome Completo</FormLabel>
                        <FormControl>
                          <Input placeholder="Nome completo" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input 
                            type="email" 
                            placeholder="email@exemplo.com" 
                            {...field}
                            disabled={isEditing}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Segunda linha: Telefone e Cargo */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="telefone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefone</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="(00) 00000-0000"
                            inputMode="tel"
                            maxLength={15}
                            value={field.value}
                            onChange={(e) => field.onChange(formatPhoneBR(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cargo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cargo</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: Gerente, Vendedor" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Campos adicionais */}
            {!isEditing && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Senha</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="Mínimo 6 caracteres" 
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="data_nascimento"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Nascimento</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Perfil de Acesso</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o perfil" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {availableRoles?.filter((role) => role.slug !== "admin_saas").map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Campo 'Empresa' removido: tenant é obtido automaticamente da sessão (TenantContext) */}
            </div>

              </form>
            </Form>
          </div>

          <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" form="user-form" disabled={mutation.isPending || isUploadingPhoto}>
              {(mutation.isPending || isUploadingPhoto) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing
                ? (mutation.isPending || isUploadingPhoto)
                  ? "Salvando..."
                  : "Salvar alterações"
                : (mutation.isPending || isUploadingPhoto)
                  ? "Criando..."
                  : "Criar usuário"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
